import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setLogoutHandler, setSessionExpiredHandler } from "./axios";
import { authApi } from "../backend_urls/authApi";
import { AuthURL } from "../routes/auth_routes/AuthURL";
import SessionExpiredModal from "../components/popups/SessionExpiredModal";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState({ isAuthenticated: false, user: null, accessToken: null });
    const [authLoading, setAuthLoading] = useState(true);
    const [isSessionExpired, setIsSessionExpired] = useState(false);

    // Standard manual logout
    const logout = useCallback(async () => {
        try { 
            await api.post(authApi.userLogout(), {}, { withCredentials: true });
        } catch (err) { 
            console.warn("Backend logout failed"); 
        }
        localStorage.removeItem("accessToken");
        localStorage.removeItem("user");
        setAuth({ isAuthenticated: false, user: null, accessToken: null });
        window.location.href = AuthURL.LOGIN;
    }, []);

    // Session expired trigger (from Axios 401 fail)
    const handleSessionExpired = useCallback(() => {
        // Save current pathname so user can return after re-login
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== AuthURL.LOGIN) {
            sessionStorage.setItem("redirectPath", currentPath);
        }
        setIsSessionExpired(true);
    }, []);

    // Redirect handler invoked after modal countdown or user clicking button
    const handleRedirectToLogin = useCallback(() => {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("user");
        setAuth({ isAuthenticated: false, user: null, accessToken: null });
        setIsSessionExpired(false);
        window.location.href = AuthURL.LOGIN;
    }, []);

    useEffect(() => { 
        setLogoutHandler(logout);
        setSessionExpiredHandler(handleSessionExpired);
    }, [logout, handleSessionExpired]);

    // Initialize Auth state
    useEffect(() => {
        const initAuth = async () => {
            const accessToken = localStorage.getItem("accessToken");
            const storedUser = localStorage.getItem("user");
            const user = storedUser ? JSON.parse(storedUser) : null;
            if (!accessToken || !user) {
                setAuthLoading(false);
                return;
            }

            try {
                const userRes = await authApi.userData();
                setAuth({ isAuthenticated: true, user: userRes.data, accessToken });
            } catch (error) { 
                handleSessionExpired();
            } finally { 
                setAuthLoading(false); 
            }
        };

        initAuth();
    }, [handleSessionExpired]);

    // Login action
    const login = ({ accessToken, user }) => {
        if (!accessToken || !user) {
            console.error("Invalid login response");
            return;
        }

        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("user", JSON.stringify(user));
        setAuth({ isAuthenticated: true, user, accessToken });
    };

    return (
        <AuthContext.Provider value = {{ ...auth, authLoading, login, logout }}>
            { children }
            {isSessionExpired && (
                <SessionExpiredModal onRedirect = { handleRedirectToLogin } />
            )}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
